import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MapPin, Building2, Zap, Users } from "lucide-react";
import { Button } from "../ui/button";

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_sanierung-live/artifacts/zdgjiqav_Komel_Startvideo.mp4";
const POSTER_URL = "https://images.pexels.com/photos/36065437/pexels-photo-36065437.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const trustBadges = [
  { icon: MapPin, text: "Regional in St. Gallen" },
  { icon: Building2, text: "Total- & Generalunternehmerin" },
  { icon: Zap, text: "Kurze Entscheidungswege" },
  { icon: Users, text: "Koordination aller Gewerke" },
];

const HeroSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="hero" 
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={POSTER_URL}
          className="absolute top-0 left-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl"
        style={{ opacity }}
      >
        <div className="max-w-4xl mx-auto text-center pt-24 md:pt-32">
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
            data-testid="hero-headline"
          >
            Bauen. Sanieren. Renovieren.
            <span className="block text-komel-accent">Alles aus einer Hand.</span>
          </motion.h1>

          {/* Subline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/90 leading-relaxed mb-10 max-w-3xl mx-auto"
            data-testid="hero-subline"
          >
            Ob privates Bauvorhaben, grösseres Objekt oder Gewerbeliegenschaft – als regionaler Partner 
            stehen wir Ihnen mit Erfahrung, Leidenschaft und kurzen Entscheidungswegen zur Seite. 
            Vom Keller bis zum Dach und rund ums Haus.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <Button
              onClick={() => scrollToSection("#kontakt")}
              className="bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold px-10 py-6 text-lg rounded-none transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              data-testid="hero-cta-primary"
            >
              Offerte anfragen
            </Button>
            <Button
              onClick={() => scrollToSection("#referenzen")}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-komel-primary font-semibold px-10 py-6 text-lg rounded-none transition-all duration-300 bg-transparent"
              data-testid="hero-cta-secondary"
            >
              Referenzen ansehen
            </Button>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-wrap justify-center gap-3 md:gap-4"
            data-testid="trust-badges"
          >
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="trust-badge"
                data-testid={`trust-badge-${index}`}
              >
                <badge.icon className="w-4 h-4" />
                <span>{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-white/80 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
